import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from '../../../i18n';
import { getSellerShops } from '../../../store/request';
import type { SellerAuthShop } from '../../../types/seller';
import { setStoreid } from '../../../utils/Request';
import { useCurrentAccount } from './currentAccountContext';

type SelectedShopContextValue = {
  shops: SellerAuthShop[];
  shopId: string | null;
  setShopId: (shopId: string) => void;
  reloadShops: () => void;
};

const SelectedShopContext = createContext<SelectedShopContextValue>({
  shops: [],
  shopId: null,
  setShopId: () => undefined,
  reloadShops: () => undefined
});

export function SelectedShopProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const account = useCurrentAccount();
  const [shops, setShops] = useState<SellerAuthShop[]>([]);
  const [shopId, setShopId] = useState<string | null>(null);
  const [shopVersion, setShopVersion] = useState(0);
  const reloadShops = useCallback(() => setShopVersion((value) => value + 1), []);

  useEffect(() => {
    const userCode = account?.userCode?.trim();
    if (!userCode) {
      setShops([]);
      setShopId(null);
      return;
    }
    let cancelled = false;
    getSellerShops({ user_code: userCode })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200 || !Array.isArray(res.content)) {
          message.error(res.message || t('layout.shop_load_failed'));
          setShops([]);
          setShopId(null);
          return;
        }
        const active = res.content.filter((item) => item.shopId && item.authCancelled !== 1);
        setShops(active);
        setShopId((current) =>
          current && active.some((item) => item.shopId === current) ? current : active[0]?.shopId ?? null
        );
      })
      .catch(() => {
        if (!cancelled) message.error(t('layout.shop_load_failed'));
      });
    return () => {
      cancelled = true;
    };
  }, [account?.userCode, shopVersion, t]);

  useEffect(() => {
    setStoreid(shopId);
    return () => setStoreid(null);
  }, [shopId]);

  const value = useMemo(() => ({ shops, shopId, setShopId, reloadShops }), [shops, shopId, reloadShops]);

  return <SelectedShopContext.Provider value={value}>{children}</SelectedShopContext.Provider>;
}

export function useSelectedShop() {
  return useContext(SelectedShopContext);
}
